import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { apiClient } from '../api/client';
import { useApi } from '../api/useApi';
import { AsyncBoundary } from '../components/AsyncState';
import { FairFlagButton } from '../components/FairFlagButton';
import { BackLink } from '../components/layout/BackLink';
import { StandPreviewCard } from '../components/StandPreviewCard';
import type { JobOffer } from '../types/api';
import { accentBackground, accentFor } from '../utils/pavilion-accent';

export function PavilionPage(): ReactElement {
  const { t } = useTranslation();
  const { pavilionSlug } = useParams<{ pavilionSlug: string }>();
  const slug = pavilionSlug ?? '';

  const pavilion = useApi(() => apiClient.getPavilion(slug), [slug]);
  const stands = useApi(() => apiClient.getPavilionStands(slug), [slug]);
  // Fetched once for the whole listing and grouped below, rather than per card.
  // Only this pavilion's stands, and every page: one page of the whole fair's
  // offers left stands further down the list with none.
  const standIds = stands.data?.map((stand) => stand.id) ?? [];
  const standKey = standIds.join(',');
  const offers = useApi(
    () => (standIds.length === 0 ? Promise.resolve([]) : apiClient.getAllOffers({ standIds })),
    [standKey],
  );

  const jobsByStandId = useMemo(() => {
    const grouped = new Map<number, JobOffer[]>();

    for (const offer of offers.data ?? []) {
      const existing = grouped.get(offer.stand.id);

      if (existing === undefined) {
        grouped.set(offer.stand.id, [offer]);
      } else {
        existing.push(offer);
      }
    }

    return grouped;
  }, [offers.data]);

  // Only redirect once the request has settled: an unresolved pavilion is
  // undefined on the first render, and redirecting then would leave every deep
  // link bouncing to the home page before its data arrived.
  if (!pavilion.loading && pavilion.error?.status === 404) {
    return <Navigate to="/" replace />;
  }

  // Bound to locals so the narrowing survives into the callback below.
  const currentPavilion = pavilion.data;
  const standList = stands.data ?? [];

  return (
    <div className="app-shell pb-12">
      <BackLink to="/" label={t('pavilion.backToCategories')} />

      <header className="mb-6">
        <h1 className="flex items-center gap-3 text-3xl font-bold text-fog-100">
          {pavilion.data?.name ?? ''}
          <FairFlagButton className="size-7" />
        </h1>
        <p className="mt-2 text-sm text-fog-500">{pavilion.data?.description}</p>
        <span
          aria-hidden="true"
          className={`mt-3 block h-1 w-16 rounded-full ${accentBackground(accentFor(pavilion.data?.position ?? 0))}`}
        />
      </header>

      <AsyncBoundary
        loading={pavilion.loading || stands.loading}
        error={pavilion.error ?? stands.error}
        onRetry={() => {
          pavilion.reload();
          stands.reload();
        }}
      >
        {currentPavilion === undefined || standList.length === 0 ? (
          <p className="text-fog-500">{t('pavilion.empty')}</p>
        ) : (
          <div className="space-y-10">
            {standList.map((stand) => (
              <StandPreviewCard
                key={stand.id}
                stand={stand}
                pavilion={currentPavilion}
                jobs={jobsByStandId.get(stand.id) ?? []}
              />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}
